import React, { useState } from 'react';
import { useToast } from '../hooks/use-toast';
import { useSite } from '../context/SiteContext';
import { useLang } from '../context/LangContext';
import { pickI18n } from '../i18n/helpers';
import { submitLead } from '../services/api';
import { Send, Mail, Phone, MapPin, Clock, CheckCircle2 } from 'lucide-react';
import AnimatedBackground from './AnimatedBackground';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const initialForm = {
  name: '',
  position: '',
  company: '',
  email: '',
  phone: '',
  service: '',
  volume: '',
  message: '',
};

const Contact = () => {
  const { content } = useSite();
  const { t, lang } = useLang();
  const contactInfo = content?.contact || {};
  const sec = content?.contactSection || {};
  const serviceOptions = (content?.formOptions?.serviceOptions || []).map((o) => pickI18n(o, lang));
  const volumeOptions = (content?.formOptions?.volumeOptions || []).map((o) => pickI18n(o, lang));

  const [form, setForm] = useState(initialForm);
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company) {
      toast({ title: t('form.missingData'), description: t('form.missingDataDesc') });
      return;
    }
    setSubmitting(true);
    try {
      await submitLead(form);
      setSent(true);
      toast({ title: t('cta.sent'), description: t('form.sentDesc') });
      setForm(initialForm);
      setTimeout(() => setSent(false), 4000);
    } catch (err) {
      toast({
        title: t('form.sendError'),
        description: err.response?.data?.detail || t('form.sendErrorDesc'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contacto" className="relative py-20 sm:py-28 bg-[#0d1b2a] overflow-hidden">
      <AnimatedBackground variant="mesh" intensity={0.5} />
      <div className="absolute left-1/2 -translate-x-1/2 top-0 w-[800px] h-[400px] rounded-full bg-[#00e0c7]/[0.05] blur-3xl" />

      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 relative">
        <div className="grid lg:grid-cols-12 gap-10 sm:gap-12">
          {/* Left: text + info */}
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#00e0c7] mb-4">
              · {pickI18n(sec.eyebrow, lang) || t('sections.contact')}
            </div>
            <h2 className="font-display text-white text-2xl sm:text-3xl lg:text-[52px] font-light tracking-tight" style={{ lineHeight: 1.25 }}>
              {pickI18n(sec.title, lang)}
            </h2>
            <p className="mt-5 sm:mt-6 text-white/60 text-[14px] sm:text-[15px] leading-relaxed max-w-md">
              {pickI18n(sec.description, lang)}
            </p>

            <div className="mt-8 sm:mt-10 space-y-4 sm:space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#00e0c7]/10 border border-[#00e0c7]/20 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-4 h-4 text-[#00e0c7]" />
                </div>
                <div className="min-w-0">
                  <div className="text-[11px] text-white/40 uppercase tracking-widest mb-1">
                    {t('sections.contactEmailLabel')}
                  </div>
                  <div className="text-white text-[14px] break-all">{contactInfo.email}</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#00e0c7]/10 border border-[#00e0c7]/20 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-4 h-4 text-[#00e0c7]" />
                </div>
                <div>
                  <div className="text-[11px] text-white/40 uppercase tracking-widest mb-1">
                    {t('sections.contactPhoneLabel')}
                  </div>
                  <div className="text-white text-[14px]">{contactInfo.phone}</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#00e0c7]/10 border border-[#00e0c7]/20 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4 text-[#00e0c7]" />
                </div>
                <div>
                  <div className="text-[11px] text-white/40 uppercase tracking-widest mb-1">
                    {t('sections.contactAddressLabel')}
                  </div>
                  <div className="text-white text-[14px]">{pickI18n(contactInfo.address, lang)}</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#00e0c7]/10 border border-[#00e0c7]/20 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-4 h-4 text-[#00e0c7]" />
                </div>
                <div>
                  <div className="text-[11px] text-white/40 uppercase tracking-widest mb-1">
                    {t('sections.contactHoursLabel')}
                  </div>
                  <div className="text-white text-[14px]">{pickI18n(contactInfo.hours, lang)}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              className="rounded-3xl border border-white/8 bg-white/[0.02] backdrop-blur-sm p-6 sm:p-8 lg:p-10"
            >
              <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
                <Field label={`${t('form.name')} *`}>
                  <input
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    className="ldc-input"
                    placeholder={t('form.placeholders.name')}
                  />
                </Field>
                <Field label={t('form.position')}>
                  <input
                    name="position"
                    value={form.position}
                    onChange={onChange}
                    className="ldc-input"
                    placeholder={t('form.placeholders.position')}
                  />
                </Field>
                <Field label={`${t('form.company')} *`} className="sm:col-span-2">
                  <input
                    name="company"
                    value={form.company}
                    onChange={onChange}
                    className="ldc-input"
                    placeholder={t('form.placeholders.company')}
                  />
                </Field>
                <Field label={`${t('form.email')} *`}>
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    className="ldc-input"
                    placeholder={t('form.placeholders.email')}
                  />
                </Field>
                <Field label={t('form.phone')}>
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    className="ldc-input"
                    placeholder={t('form.placeholders.phone')}
                  />
                </Field>

                <Field label={t('form.service')}>
                  <Select
                    value={form.service}
                    onValueChange={(v) => setForm({ ...form, service: v })}
                  >
                    <SelectTrigger className="ldc-select-trigger">
                      <SelectValue placeholder={t('form.selectService')} />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0a1420] border-white/10 text-white">
                      {serviceOptions.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <Field label={pickI18n(sec.volumeLabel, lang) || t('form.volume')}>
                  <Select
                    value={form.volume}
                    onValueChange={(v) => setForm({ ...form, volume: v })}
                  >
                    <SelectTrigger className="ldc-select-trigger">
                      <SelectValue placeholder={t('form.selectVolume')} />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0a1420] border-white/10 text-white">
                      {volumeOptions.map((v) => (
                        <SelectItem key={v} value={v}>
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <Field label={t('form.message')} className="sm:col-span-2">
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    rows={4}
                    className="ldc-input resize-none"
                    placeholder={t('form.placeholders.message')}
                  />
                </Field>
              </div>

              <div className="mt-6 sm:mt-8 flex items-start sm:items-center justify-between flex-wrap gap-4">
                <p className="text-[12px] text-white/40 max-w-md">
                  {t('sections.formDisclaimer')}
                </p>
                <button
                  type="submit"
                  disabled={sent || submitting}
                  className={`group inline-flex items-center gap-2 px-6 sm:px-8 h-12 sm:h-14 rounded-full font-semibold text-[14px] sm:text-[15px] transition-all ${
                    sent
                      ? 'bg-[#00e0c7]/20 text-[#9be8dc] cursor-default'
                      : 'bg-[#00e0c7] hover:bg-[#00c4ad] text-[#0d1b2a] shadow-[0_10px_40px_-10px_rgba(0,224,199,0.5)]'
                  }`}
                >
                  {sent ? (
                    <>
                      <CheckCircle2 className="w-5 h-5" />
                      {t('cta.sent')}
                    </>
                  ) : submitting ? (
                    <>{t('cta.sending')}</>
                  ) : (
                    <>
                      {pickI18n(sec.submitLabel, lang) || t('cta.submitForm')}
                      <Send className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, children, className = '' }) => (
  <label className={`block ${className}`}>
    <span className="block text-[11px] text-white/50 uppercase tracking-widest mb-2">
      {label}
    </span>
    {children}
  </label>
);

export default Contact;
